import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import Calendar from '../components/Calendar';
import AppointmentsList from '../components/AppointmentsList';
import TodoList from '../components/TodoList';
import VisitSummaryButton from '../components/VisitSummaryButton';
import AppointmentModal from '../components/AppointmentModal';
import { collection, query, where, getDocs, orderBy, deleteDoc, doc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { useAuth } from '../contexts/AuthContext';
import { Calendar as CalendarIcon, CheckSquare, Clock, Plus } from 'lucide-react';

const Dashboard = () => {
  const { currentUser } = useAuth();
  const [appointments, setAppointments] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [loading, setLoading] = useState(true);
  const [showAppointmentModal, setShowAppointmentModal] = useState(false);
  const [editingAppointment, setEditingAppointment] = useState(null);

  // Fetch appointments from Firestore
  useEffect(() => {
    const fetchAppointments = async () => {
      if (!currentUser) return;

      try {
        setLoading(true);
        const appointmentsRef = collection(db, 'appointments');
        const q = query(
          appointmentsRef,
          where('userId', '==', currentUser.uid),
          orderBy('date', 'asc')
        );

        const querySnapshot = await getDocs(q);
        const appointmentsData = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
        }));

        setAppointments(appointmentsData);
      } catch (error) {
        console.error('Error fetching appointments:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchAppointments();
  }, [currentUser]);

  // Get upcoming appointments (from today onwards)
  const upcomingAppointments = appointments
    .filter(apt => {
      // Parse the date properly
      const aptDate = new Date(apt.startDate || apt.date);
      aptDate.setHours(0, 0, 0, 0); // Reset time to start of day
      
      const today = new Date();
      today.setHours(0, 0, 0, 0); // Reset time to start of day
      
      return aptDate >= today; // Now includes same-day appointments
    })
    .slice(0, 5);

  const handleEditAppointment = (appointment) => {
    setEditingAppointment(appointment);
    setShowAppointmentModal(true);
  };

  const handleDeleteAppointment = async (appointmentId) => {
    try {
      const appointmentRef = doc(db, 'appointments', appointmentId);
      await deleteDoc(appointmentRef);
      
      // Refresh appointments
      const appointmentsRef = collection(db, 'appointments');
      const q = query(
        appointmentsRef,
        where('userId', '==', currentUser.uid),
        orderBy('date', 'asc')
      );
      const querySnapshot = await getDocs(q);
      const appointmentsData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      }));
      setAppointments(appointmentsData);
      
      // Show success message
      alert('✅ Appointment deleted successfully!');
    } catch (error) {
      console.error('Error deleting appointment:', error);
      alert('❌ Failed to delete appointment. Please try again.');
    }
  };

  const handleCreateFromCalendar = (date) => {
    setSelectedDate(date);
    setEditingAppointment(null);
    setShowAppointmentModal(true);
  };

  return (
    <Layout>
      <div className="space-y-6">
        {/* Welcome Header */}
        <div className="bg-gradient-to-r from-pastel-pink via-pastel-blue to-pastel-purple rounded-2xl p-6 shadow-md">
          <h1 className="text-3xl font-bold text-brand-brown mb-2">
            Welcome back! 🐾
          </h1>
          <p className="text-brand-brown/70">
            You have {upcomingAppointments.length} upcoming appointments today
          </p>
        </div>

        {/* Main Dashboard Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Appointments Section */}
          <div className="card p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-2">
                <Clock className="h-5 w-5 text-pastel-blue" />
                <h2 className="text-xl font-bold text-brand-brown">Appointments</h2>
              </div>
              <button
                onClick={() => {
                  setEditingAppointment(null);
                  setShowAppointmentModal(true);
                }}
                className="btn-primary text-sm flex items-center space-x-1"
              >
                <Plus className="h-4 w-4" />
                <span>New</span>
              </button>
            </div>
            
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-pastel-blue"></div>
              </div>
            ) : (
              <AppointmentsList 
                appointments={upcomingAppointments}
                onEdit={handleEditAppointment}
                onDelete={handleDeleteAppointment}
              />
            )}
          </div>

          {/* To-Do List Section */}
          <div className="card p-6">
            <div className="flex items-center space-x-2 mb-4">
              <CheckSquare className="h-5 w-5 text-pastel-green" />
              <h2 className="text-xl font-bold text-brand-brown">To-Do List</h2>
            </div>
            <TodoList />
          </div>
        </div>

        {/* Calendar Section */}
        <div className="card p-6">
          <div className="flex items-center space-x-2 mb-4">
            <CalendarIcon className="h-5 w-5 text-pastel-purple" />
            <h2 className="text-xl font-bold text-brand-brown">Calendar</h2>
          </div>
          
          <Calendar 
            appointments={appointments}
            selectedDate={selectedDate}
            onDateSelect={setSelectedDate}
            onDateDoubleClick={handleCreateFromCalendar}
            onDeleteAppointment={handleDeleteAppointment}
          />

          {/* Legend */}
          <div className="mt-6 flex flex-wrap gap-4">
            <div className="flex items-center space-x-2">
              <div className="w-4 h-4 rounded bg-service-daycare"></div>
              <span className="text-sm text-brand-brown/70">Doggy Day Care</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-4 h-4 rounded bg-service-sitting"></div>
              <span className="text-sm text-brand-brown/70">Pet Sitting</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-4 h-4 rounded bg-service-walking"></div>
              <span className="text-sm text-brand-brown/70">Dog Walking</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-4 h-4 rounded bg-service-checkin"></div>
              <span className="text-sm text-brand-brown/70">Check Ins</span>
            </div>
            <button
              onClick={() => handleCreateFromCalendar(new Date())}
              className="ml-auto btn-secondary text-sm flex items-center space-x-1"
            >
              <Plus className="h-4 w-4" />
              <span>Add Appointment</span>
            </button>
          </div>
        </div>

        {/* Leave After Visit Summary Button */}
        <VisitSummaryButton />

        {/* Appointment Modal */}
        <AppointmentModal
          isOpen={showAppointmentModal}
          onClose={() => {
            setShowAppointmentModal(false);
            setEditingAppointment(null);
            // Refresh appointments
            const fetchAppointments = async () => {
              const appointmentsRef = collection(db, 'appointments');
              const q = query(
                appointmentsRef,
                where('userId', '==', currentUser.uid),
                orderBy('date', 'asc')
              );
              const querySnapshot = await getDocs(q);
              const appointmentsData = querySnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data(),
              }));
              setAppointments(appointmentsData);
            };
            fetchAppointments();
          }}
          editingAppointment={editingAppointment}
          selectedDate={selectedDate}
        />
      </div>
    </Layout>
  );
};

export default Dashboard;
